import { useGetAlphaBscPositionsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { fmtPrice } from "@/lib/format";

/**
 * Closed positions history with realized P&L attribution.
 * Shows exit reason (TP1/TP2/trail/hard_stop/graduation_event/manual)
 * so we can study which exit types are profitable over time.
 */
export default function AlphaBscHistoryPage() {
  const closed = useGetAlphaBscPositionsQuery("closed", { pollingInterval: 60000 });
  const list = closed.data || [];

  // Aggregate stats
  const totalPnl = list.reduce((s: number, p: any) => s + Number(p.realized_pnl_usd || 0), 0);
  const wins = list.filter((p: any) => Number(p.realized_pnl_usd || 0) > 0);
  const losses = list.filter((p: any) => Number(p.realized_pnl_usd || 0) < 0);
  const winRate = list.length > 0 ? (wins.length / list.length) * 100 : 0;
  const totalInvested = list.reduce((s: number, p: any) => s + Number(p.entry_size_usdt || 0), 0);
  const totalProceeds = list.reduce((s: number, p: any) => s + Number(p.exit_proceeds_usdt || 0), 0);

  // Exit reason breakdown
  const byReason: Record<string, { count: number; pnl: number }> = {};
  for (const p of list) {
    const reason = p.auto_exit_reason || "manual";
    if (!byReason[reason]) byReason[reason] = { count: 0, pnl: 0 };
    byReason[reason].count += 1;
    byReason[reason].pnl += Number(p.realized_pnl_usd || 0);
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Closed positions</h1>
        <p className="text-sm text-muted">
          Realized P&L history with exit-reason attribution. Use this to
          measure which exit type (graduation event, tier targets, hard
          stop, trail) is producing the gains over time.
        </p>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total closed" value={String(list.length)} sub={`${wins.length} wins, ${losses.length} losses`} />
        <Kpi label="Win rate" value={`${winRate.toFixed(1)}%`} />
        <Kpi label="Net realized P&L"
             value={`${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(4)}`}
             tone={totalPnl > 0 ? "good" : totalPnl < 0 ? "bad" : "muted"} />
        <Kpi label="Turnover" value={`$${totalInvested.toFixed(2)} → $${totalProceeds.toFixed(2)}`} />
      </div>

      {/* Exit reason breakdown */}
      {Object.keys(byReason).length > 0 && (
        <Card>
          <CardHeader><CardTitle>By exit reason</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
              {Object.entries(byReason).map(([reason, st]) => (
                <div key={reason} className="rounded border p-2 bg-muted/5">
                  <div className="text-xs text-muted">{reason}</div>
                  <div className="font-semibold">{st.count} trades</div>
                  <div className={`text-xs tabular-nums ${st.pnl > 0 ? "text-green-500" : st.pnl < 0 ? "text-red-500" : ""}`}>
                    {st.pnl >= 0 ? "+" : ""}${st.pnl.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* TABLE */}
      <Card>
        <CardHeader>
          <CardTitle>Closed positions ({list.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {closed.isLoading && <Skeleton className="h-32 w-full" />}
          {!closed.isLoading && list.length === 0 && (
            <div className="text-sm text-muted italic">
              No closed positions yet.
            </div>
          )}
          {list.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Closed</TH>
                  <TH>Symbol</TH>
                  <TH>Exit reason</TH>
                  <TH className="text-right">Entry $</TH>
                  <TH className="text-right">Exit $</TH>
                  <TH className="text-right">Δ%</TH>
                  <TH className="text-right">P&L</TH>
                  <TH>Auto?</TH>
                  <TH>Tx</TH>
                </TR>
              </THead>
              <TBody>
                {list.slice(0, 100).map((p: any) => {
                  const dp = p.entry_price_usd > 0 && p.exit_price_usd
                    ? ((p.exit_price_usd / p.entry_price_usd) - 1) * 100 : 0;
                  const pnl = Number(p.realized_pnl_usd || 0);
                  const reason = p.auto_exit_reason || "manual";
                  const reasonTone = reason === "graduation_event" ? "good"
                                    : reason === "hard_stop" ? "bad"
                                    : reason.includes("trail") ? "info"
                                    : "muted";
                  return (
                    <TR key={p.id}>
                      <TD className="text-xs whitespace-nowrap">{p.exit_ts?.slice(5,16) ?? "—"}</TD>
                      <TD className="font-mono">{p.symbol}</TD>
                      <TD><Badge tone={reasonTone as any}>{reason}</Badge></TD>
                      <TD className="text-right tabular-nums font-mono text-xs">{fmtPrice(Number(p.entry_price_usd))}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">{fmtPrice(Number(p.exit_price_usd ?? 0))}</TD>
                      <TD className={`text-right tabular-nums ${dp >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {dp >= 0 ? "+" : ""}{dp.toFixed(2)}%
                      </TD>
                      <TD className={`text-right tabular-nums font-semibold ${pnl > 0 ? "text-green-500" : pnl < 0 ? "text-red-500" : ""}`}>
                        {pnl >= 0 ? "+" : ""}${pnl.toFixed(4)}
                      </TD>
                      <TD className="text-xs">{p.auto_managed ? <Badge tone="info">auto</Badge> : <span className="text-muted">manual</span>}</TD>
                      <TD className="text-xs">
                        {p.exit_tx_hash && (
                          <a href={`https://bscscan.com/tx/${p.exit_tx_hash}`} target="_blank" rel="noopener noreferrer"
                             className="hover:underline text-muted font-mono">
                            {p.exit_tx_hash.slice(0,8)}…
                          </a>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: {
  label: string; value: string; sub?: string;
  tone?: "good" | "bad" | "muted";
}) {
  const colors = { good: "text-green-500", bad: "text-red-500", muted: "" };
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted">{label}</div>
        <div className={`text-xl font-semibold tabular-nums ${colors[tone ?? "muted"]}`}>{value}</div>
        {sub && <div className="text-[11px] text-muted mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}
